import React, { useState } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import Header from '../Header/Header';
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH, SidebarQuickStats } from '../Sidebar/Sidebar';
import { AccessContext } from '../../../utils/permissions';

interface LayoutProps {
  children: React.ReactNode;
  currentPath?: string;
  user?: {
    name: string;
    email: string;
    avatar?: string;
    role: string;
  };
  notifications?: {
    count: number;
    items: Array<{
      id: string;
      title: string;
      message: string;
      time: string;
      severity: 'info' | 'warning' | 'error';
    }>;
  };
  onNavigate?: (path: string) => void;
  onLogout?: () => void;
  quickStats?: SidebarQuickStats;
  /** Permissions/ownership used to filter the sidebar. */
  access?: AccessContext;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  currentPath = '/dashboard',
  user,
  notifications,
  onNavigate,
  onLogout,
  quickStats,
  access,
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen,      setSidebarOpen]      = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const offset = isMobile ? 0 : (sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH);

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentPath={currentPath}
        onNavigate={onNavigate}
        onLogout={onLogout}
        collapsed={!isMobile && sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(prev => !prev)}
        quickStats={quickStats}
        access={access}
      />

      <Header
        onMenuToggle={() => setSidebarOpen(prev => !prev)}
        showMenuButton={isMobile}
        user={user}
        notifications={notifications}
        onLogout={onLogout}
        onNavigate={onNavigate}
        contentOffset={offset}
      />

      {/* Main content */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: '56px', md: '64px' },
          left: `${offset}px`,
          right: 0,
          bottom: 0,
          overflowY: 'auto',
          background: 'linear-gradient(160deg, #EEF2F9 0%, #E8EEF8 60%, #EDF1F9 100%)',
          transition: 'left 0.22s ease',
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: 3 },
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 2.5, md: 3 }, py: { xs: 2, md: 2.5 }, width: '100%', boxSizing: 'border-box' }}>
          {children}
        </Box>
      </Box>

    </Box>
  );
};

export default Layout;
